import React from "react";
import { DownOutlined } from "@ant-design/icons";
import { SbTable, SbTooltip } from "uiCore";

import {
  TableShell,
  TableScrollWrapper,
  ExpandedContent,
  ExpandedContentHeader,
  MicrotaskList,
  MicrotaskItem,
  EmptyMicrotasks,
  ExpandIndicator,
  PotentialValue,
} from "./SbPACGroupAgentDetailsV2.styles";

// PRD §5.12 — tooltip content strings for Agent Details table
const TOOLTIP_ROLE_ROW =
  "Expand to see the specific skills and tasks within this role that this agent will impact.";
const TOOLTIP_EXPAND_ROW =
  "Show the detailed skills breakdown for this role — including which specific tasks the agent automates.";

const compareText = (key) => (a, b) =>
  String(a?.[key] ?? "").localeCompare(String(b?.[key] ?? ""), undefined, {
    sensitivity: "base",
  });

const compareNumber = (key) => (a, b) =>
  Number(a?.[key] ?? 0) - Number(b?.[key] ?? 0);

const PACGroupAgentDetailsTableV2 = ({
  loading,
  rows = [],
  expandedRowKeys = [],
  onToggleRow,
  formatPercentage,
}) => {
  const columns = [
    {
      title: "#",
      dataIndex: "__rowIndex",
      key: "__rowIndex",
      width: 60,
      align: "center",
      filterable: false,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Task",
      dataIndex: "parent_task",
      key: "parent_task",
      sorter: compareText("parent_task"),
      sortDirections: ["ascend", "descend"],
      render: (value) => value || "-",
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 180,
      sorter: compareText("category"),
      sortDirections: ["ascend", "descend"],
      render: (value) => value || "General Task",
    },
    {
      title: "Job Title",
      dataIndex: "job_title",
      key: "job_title",
      width: 220,
      sorter: compareText("job_title"),
      sortDirections: ["ascend", "descend"],
      render: (value) => value || "-",
    },
    {
      title: "AI Potential",
      dataIndex: "ai_potential",
      key: "ai_potential",
      width: 160,
      align: "center",
      sorter: compareNumber("ai_potential"),
      defaultSortOrder: "descend",
      sortDirections: ["descend", "ascend"],
      filterLabel: (value) => formatPercentage(value),
      render: (value) => (
        <PotentialValue>{formatPercentage(value)}</PotentialValue>
      ),
    },
    {
      title: "",
      dataIndex: "__expand",
      key: "__expand",
      width: 52,
      align: "center",
      filterable: false,
      render: (_, record) => (
        <SbTooltip content={TOOLTIP_EXPAND_ROW}>
          <ExpandIndicator
            $expanded={expandedRowKeys.includes(record.__rowKey)}
          >
            <DownOutlined />
          </ExpandIndicator>
        </SbTooltip>
      ),
    },
  ];

  return (
    <TableShell>
      <TableScrollWrapper>
        <SbTable
          className="agent-details-table"
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={false}
          rowKey={(record) => record.__rowKey}
          onRow={(record) => ({
            onClick: () => onToggleRow(record),
            title: TOOLTIP_ROLE_ROW,
          })}
          expandable={{
            expandedRowKeys,
            showExpandColumn: false,
            rowExpandable: () => true,
            expandedRowRender: (record) => {
              const microtasks = record?.microtasks || [];

              return (
                <ExpandedContent>
                  <ExpandedContentHeader>Microtasks</ExpandedContentHeader>

                  {microtasks.length > 0 ? (
                    <MicrotaskList>
                      {microtasks.map((microtask, index) => (
                        <MicrotaskItem
                          key={
                            microtask?.sb_unique_id ||
                            `${record.__rowKey}-micro-${index}`
                          }
                        >
                          {microtask?.micro_task || "Untitled microtask"}
                        </MicrotaskItem>
                      ))}
                    </MicrotaskList>
                  ) : (
                    <EmptyMicrotasks>No microtasks found.</EmptyMicrotasks>
                  )}
                </ExpandedContent>
              );
            },
          }}
          locale={{
            emptyText: loading
              ? " "
              : "No tasks available for this agent.",
          }}
        />
      </TableScrollWrapper>
    </TableShell>
  );
};

export default PACGroupAgentDetailsTableV2;